import { gettext } from '@eventbrite/i18n';
import { getFormattedEventDate as formatEventDate } from '@eventbrite/event-date-time';

import memoize from '../../helpers/memoize';
import { PRINT_OPTIONS } from '../../constants/pdf-constants';

export const parseSeatInfo = ({ seatInfo }) => {
    const { ids = [], rowLabel, unitLabel } = seatInfo;
    const parsedRowLabel =
        ids.length > 1 && !rowLabel ? gettext('Multi') : rowLabel;
    const parsedUnitLabel = ids.length > 1 ? gettext('Multi') : unitLabel;

    return {
        ...seatInfo,
        rowLabel: parsedRowLabel,
        unitLabel: parsedUnitLabel,
    };
};

export const formatTiersForLegendDialog = ({ tiers = {} }) => {
    const parsedTiers = [];

    Object.keys(tiers).forEach((key) =>
        parsedTiers.push({
            id: key,
            name: tiers[key].name || '',
            color: tiers[key].color,
        }),
    );

    return parsedTiers;
};

export const getVariantsNamesLabel = memoize((variants = []) =>
    variants.map((variant) => variant.ticketName).join(', '),
);

export const parseSectionFloatingDialogSectionInfo = ({
    hoverSectionInfo = {},
    totalUnitsInfo = {},
}) => {
    const { hoverSectionTitle = '', hoverSectionId = '' } = hoverSectionInfo;
    const { available = 0, total = 0 } = totalUnitsInfo;

    return {
        title: hoverSectionTitle,
        unitsAvailable: available,
        unitsTotal: total,
        id: hoverSectionId,
    };
};

export const parseSectionHoldsVariants = memoize((sectionHoldsVariants) =>
    sectionHoldsVariants.map((hold) => ({
        id: hold.id,
        name: hold.name,
        heldQuantity: `${hold.totalHeldUnitsInSection}`,
        soldQuantity: `${hold.soldHeldQuantity}`,
    })),
);

export const parseSectionFloatingDialogTiers = (tiers) =>
    tiers.map((tier) => ({
        ...tier,
        seatsAvailableLabel: gettext(
            '%(quantityAvailable)s/%(quantityTotal)s seats available',
            {
                quantityTotal: tier.quantityTotal,
                quantityAvailable: tier.quantityAvailable,
            },
        ),
        priceLabel: gettext('From %(price)s', {
            price: tier.price,
        }),
    }));

export const getCheckoutWidgetParentUrl = () =>
    encodeURIComponent(window.location.href);

const _getOrderReportUrl = ({ eventId, orderId }) => {
    let reportUrl = '';

    if (window && eventId && orderId) {
        reportUrl = window.location.origin;

        reportUrl += `/reports?filterby=all,${orderId}&rid=h&eid=${eventId}`;
    }

    return reportUrl;
};

export const goToReportsPage = ({ eventId, orderId }) => {
    if (window && eventId && orderId) {
        const reportUrl = _getOrderReportUrl({ eventId, orderId });

        window.location.href = reportUrl;
    }
};

export const getPrintOptions = ({ getPrintOptionValue }) =>
    PRINT_OPTIONS.map((printOption) => {
        return {
            ...printOption,
            value: getPrintOptionValue(printOption.id),
        };
    });

export const getManageOrderUrl = ({ soldSeatInfo = {} }) => {
    const { order_id: orderId, event_id: eventId } = soldSeatInfo;

    return _getOrderReportUrl({ eventId, orderId });
};

export const parseSoldSeatInfo = ({ soldSeatInfo = {}, seatInfo = {} }) => {
    const { rowLabel, sectionLabel, tierColor, unitLabel } = seatInfo;
    const {
        profile: { first_name: firstName = '', last_name: lastName = '' } = {},
        costs: { gross: { display = '' } = {} } = {},
        order_id: orderId,
        event_id: eventId,
    } = soldSeatInfo;
    const orderUrl = `/reports?filterby=all,${orderId}&rid=h&eid=${eventId}`;

    return {
        username: `${firstName} ${lastName}`,
        section: sectionLabel,
        price: display,
        row: rowLabel,
        seat: unitLabel,
        tier: tierColor,
        link: orderUrl,
    };
};

export const parseReleaseHoldInfo = ({ seatInfo }) => ({
    id: seatInfo.ids[0],
});

export const getFormattedEventDate = ({ start, end }, locale) => {
    let formattedEventDate = '';

    if (start && end) {
        const startFullDateWeekday = formatEventDate({
            event: { start, end },
            locale,
        });

        formattedEventDate = gettext('%(startFullDateWeekday)s', {
            startFullDateWeekday,
        });
    }

    return formattedEventDate;
};

export const goToPreviousPage = () => window && window.history.back();
